import i18next from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { IRagConfigListItem, RagStatus } from "../../@types/Rag";
import { CircleChecked } from "../../assets/icons/CircleChecked";
import { CircleUnchecked } from "../../assets/icons/CircleUncheked";
import { IOption, SelectDropdown } from "../../components/SelectDropdown";
import { SkeletonLoading } from "../../components/Skeleton";
import { createColumn, Table } from "../../components/Table";
import { TableSkeleton } from "../../components/TableSkeleton";
import {
  getActiveHospitalGroup,
  useHospitalGroups,
  useOwnHospitalGroup,
  useRagConfigList,
} from "../../hooks/queries/ragConfig";
import {
  useAvailableWards,
  useHospitalGroupWards,
} from "../../hooks/queries/wards";
import { useCurrentUserRole } from "../../hooks/useCurrentUserRole";
import { PageLayout } from "../../layout/components/PageLayout";
import { PageTitle } from "../../layout/components/PageTitle";
import { mixpanelActions } from "../../utils/mixpanel";
import { getWardLabel } from "../../utils/wardLabel";
import {
  AddRagButton,
  HospitalGroupSelectCss,
  ListContainer,
  ListTableContainer,
  PageTitleContainer,
  PMPHospitalGroupSelect,
} from "./RagConfigList.styles";

interface IRagConfigRow {
  id: string;
  configNumber: number;
  wards: string;
  published: React.ReactElement;
  rawStatus: RagStatus;
}

const ragListColumns = [
  createColumn<IRagConfigRow>({
    keyName: "configNumber",
    header: () => i18next.t("rag_columns_configNumber"),
    cell: (info) => info.renderValue(),
    size: 80,
  }),
  createColumn<IRagConfigRow>({
    keyName: "wards",
    header: () => getWardLabel().plural,
    cell: (info) => info.renderValue(),
  }),
  createColumn<IRagConfigRow>({
    keyName: "published",
    header: () => i18next.t("rag_columns_published"),
    cell: (info) => info.renderValue(),
    size: 50,
  }),
];

export function RagConfigListPage() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { canViewRagTab, isSuperUser } = useCurrentUserRole();

  const { hospitalGroups, isLoading: isHospitalGroupsLoading } =
    useHospitalGroups(isSuperUser);
  const hospitalGroupOptions = useMemo<IOption[]>(
    () =>
      hospitalGroups.map((group) => ({
        label: group.hospitalGroupName,
        value: group.hospitalGroupId.toString(),
        key: `hg-${group.hospitalGroupId}`,
      })),
    [hospitalGroups]
  );
  const [selectedHospitalGroup, setSelectedHospitalGroup] = useState<
    string | undefined
  >(undefined);

  const { hospitalGroup: ownHospitalGroup } = useOwnHospitalGroup();
  const activeGroup = getActiveHospitalGroup();

  // Default: user's active hospital group (intercomDetails) with fallback to
  // ownHospitalGroup. Super users can override via the dropdown.
  const defaultGroupId =
    activeGroup?.id ?? ownHospitalGroup?.hospitalGroupId ?? undefined;
  const superUserSelectedId = selectedHospitalGroup
    ? Number.parseInt(selectedHospitalGroup, 10)
    : undefined;
  const effectiveHospitalGroupId =
    isSuperUser && superUserSelectedId !== undefined
      ? superUserSelectedId
      : defaultGroupId;

  const { configs, isLoading, isFetching } = useRagConfigList(
    effectiveHospitalGroupId
  );

  const {
    availableWards,
    isWardsLoading: isAvailableWardsLoading,
    isWardsFetching: isAvailableWardsFetching,
  } = useAvailableWards();
  const {
    hospitalGroupWards,
    isWardsLoading: isHospitalGroupWardsLoading,
    isWardsFetching: isHospitalGroupWardsFetching,
  } = useHospitalGroupWards(
    isSuperUser && effectiveHospitalGroupId ? effectiveHospitalGroupId : 0
  );
  const wardSource =
    isSuperUser && effectiveHospitalGroupId
      ? hospitalGroupWards ?? []
      : availableWards ?? [];
  const isWardSourceLoading =
    isSuperUser && effectiveHospitalGroupId
      ? isHospitalGroupWardsLoading || isHospitalGroupWardsFetching
      : isAvailableWardsLoading || isAvailableWardsFetching;

  const wardNameById = useMemo(() => {
    const map = new Map<number, string>();
    wardSource.forEach((w) => map.set(w.wardId, w.wardName));
    return map;
  }, [wardSource]);

  useEffect(() => {
    mixpanelActions.track("PageView: RagConfigList");
  }, []);

  // Pre-select the user's default group in the super-user dropdown so the
  // table loads with data on first render.
  useEffect(() => {
    if (
      isSuperUser &&
      defaultGroupId !== undefined &&
      selectedHospitalGroup === undefined
    ) {
      setSelectedHospitalGroup(String(defaultGroupId));
    }
  }, [isSuperUser, defaultGroupId, selectedHospitalGroup]);

  const renderWardsLabel = (ids: number[]) => {
    if (ids.length === 0) return "-";
    return ids
      .map((id) => wardNameById.get(id) ?? `#${id}`)
      .sort((a, b) => a.localeCompare(b))
      .join(", ");
  };

  const tableContent = useMemo<IRagConfigRow[]>(() => {
    return configs.map((c: IRagConfigListItem) => ({
      id: c.id,
      configNumber: c.configNumber,
      wards: renderWardsLabel(c.wards),
      published: (
        <div style={{ marginLeft: "30px" }}>
          {c.status === "published" ? <CircleChecked /> : <CircleUnchecked />}
        </div>
      ),
      rawStatus: c.status,
    }));
  }, [configs, wardNameById]);

  const handleAdd = () => {
    if (!effectiveHospitalGroupId) return;
    navigate(`/rag/new?hg=${effectiveHospitalGroupId}`);
  };

  const handleRowClick = (row: IRagConfigRow) => {
    navigate(`/rag/${row.id}`);
  };

  if (!canViewRagTab) {
    return <Navigate to="/patients/active" replace />;
  }

  const isLoadingTable = isLoading || isFetching || isWardSourceLoading;

  return (
    <PageLayout>
      <ListContainer>
        <PageTitleContainer>
          <PageTitle>{t("rag_title")}</PageTitle>
        </PageTitleContainer>

        <ListTableContainer>
          {isSuperUser ? (
            <PMPHospitalGroupSelect>
              {isHospitalGroupsLoading ? (
                <SkeletonLoading
                  height="26px"
                  width="160px"
                  borderRadius="12px"
                />
              ) : (
                <SelectDropdown
                  options={hospitalGroupOptions}
                  height={30}
                  placeholder={t("rag_selectHospital")}
                  optionFilterProp="label"
                  value={selectedHospitalGroup}
                  onValueChange={(v) => setSelectedHospitalGroup(v.toString())}
                  className={HospitalGroupSelectCss()}
                />
              )}
            </PMPHospitalGroupSelect>
          ) : null}

          <AddRagButton
            data-disabled={!effectiveHospitalGroupId}
            onClick={handleAdd}
          >
            {t("rag_add")}
          </AddRagButton>

          {isLoadingTable ? (
            <TableSkeleton columnsNumber={3} rowsNumber={6} />
          ) : (
            <Table
              data={tableContent}
              emptyStateText={t("rag_emptyState")}
              columns={ragListColumns}
              showMaxContent
              onRowClick={handleRowClick}
            />
          )}
        </ListTableContainer>
      </ListContainer>
    </PageLayout>
  );
}
